import { cn } from '../../lib/cn'
import { Spinner } from './Spinner'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
}

// Variants per design-system §6.1. Amber CTA carries dark-on-amber text (AAA).
// Hover/active styles are enabled:-guarded and disabled primary desaturates
// toward the surface ramp (§6.1: disabled must read unambiguously inert, not
// a half-opacity CTA that still lights up on hover).
const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-ember-500 text-text-on-ember shadow-warm enabled:hover:bg-ember-400 enabled:hover:shadow-glow enabled:active:bg-ember-600 disabled:bg-ember-700 disabled:shadow-none',
  secondary:
    'bg-surface text-text ring-1 ring-border enabled:hover:bg-surface-raised enabled:hover:ring-border-warm',
  ghost: 'text-text-muted enabled:hover:text-text enabled:hover:bg-surface/60',
  destructive: 'text-danger-fg ring-1 ring-danger/40 enabled:hover:bg-danger/12',
}

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-5 text-base',
  lg: 'h-14 px-7 text-lg',
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        // whitespace-nowrap: a CTA never wraps mid-label (§6.1 stable width) —
        // tight rows wrap BETWEEN actions (flex-wrap on the container).
        'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-control font-medium transition duration-200 ease-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      disabled={disabled || loading}
      aria-disabled={disabled || loading || undefined}
      {...props}
    >
      {loading && <Spinner size="sm" className="text-current" aria-hidden="true" role="presentation" />}
      {children}
    </button>
  )
}
